/**
 * @module UseHypothesisLibrary
 * @purpose Hook for fetching the full platform hypothesis library (all registered
 *   predicates), independent of any case's evaluation state.
 * @depends services/reasoning (listHypothesisLibrary), types/reasoning
 * @dependedOnBy pages/case/analysis/reasoning/hypothesis/CatalogueSidebar
 *
 * @rules
 * - Performance: fetches once on mount; requestIdRef guards against out-of-order writes
 *     if refetch() is called again before a prior request settles
 * - State: local useState; no shared context
 * - Errors: error shape is string | null; ApiError.message extracted in the catch block
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { listHypothesisLibrary } from '../services/reasoning'
import type { HypothesisLibraryEntry } from '../types/reasoning'

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

export interface UseHypothesisLibraryResult {
  predicates: HypothesisLibraryEntry[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Fetch the platform-wide hypothesis library once on mount.
 *
 * @behaviour No params — the endpoint is global. Stale-request guard (requestIdRef)
 *   prevents out-of-order state writes if refetch() overlaps a prior in-flight request.
 * @errors Sets error string on failure; clears predicates to [] on failure.
 */
export function useHypothesisLibrary(): UseHypothesisLibraryResult {
  const [predicates, setPredicates] = useState<HypothesisLibraryEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const requestIdRef = useRef(0)

  const fetch = useCallback(async () => {
    requestIdRef.current += 1
    const thisId = requestIdRef.current

    setLoading(true)
    setError(null)

    try {
      const data = await listHypothesisLibrary()
      if (thisId !== requestIdRef.current) return
      setPredicates(data.predicates)
    } catch (err) {
      if (thisId !== requestIdRef.current) return
      setError(messageOf(err, 'Failed to fetch hypothesis library'))
      setPredicates([])
    } finally {
      if (thisId === requestIdRef.current) setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { predicates, loading, error, refetch: fetch }
}
